import { useCallback, useMemo, useState } from 'react';
import type { StorePlan } from '@shared/routing.ts';
import { getStore, useAppState } from '../../data/store.ts';
import { Sheet, SheetHeader, money } from '../../components/ui.tsx';
import { Icon } from '../../components/Icon.tsx';
import { Link, navigate } from '../../lib/router.tsx';
import { useCartPlan } from '../../lib/hooks.ts';
import { hasCartLink, prepareCart, type CartOpenResult } from './cartLinks.ts';

function deliveryLabel(hours: number): string {
  if (hours <= 8) return 'Today';
  if (hours <= 30) return 'Tomorrow';
  return `${Math.round(hours / 24)} days`;
}

function StoreCard({ plan }: { plan: StorePlan }) {
  const store = getStore();
  const stores = useAppState((s) => s.stores);
  const linkStore = hasCartLink(plan.storeId);
  const [opened, setOpened] = useState<CartOpenResult | null>(null);
  // Stores without a cart link show their checklist straight away.
  const checklistOnly = useMemo(() => (linkStore ? null : prepareCart(plan)), [linkStore, plan]);
  const result = opened ?? checklistOnly;
  const ready = plan.status === 'ready';
  const estimate = plan.subtotal + plan.fee;
  const names = plan.lines.map((l) => l.item.name);
  const summary = names.length > 6 ? `${names.slice(0, 6).join(', ')} +${names.length - 6}` : names.join(', ');

  const open = () => {
    const r = prepareCart(plan);
    setOpened(r);
    if (r.url) window.open(r.url, '_blank', 'noopener');
  };

  return (
    <div className={`card stack-lg ${ready ? 'card-accent' : ''}`}>
      <div className="row-between">
        <span style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700 }}>{plan.storeName}</span>
        <span className={`badge ${ready ? 'badge-gold' : 'badge-orange'}`}>{ready ? 'Ready to check out' : 'Holding'}</span>
      </div>

      {ready ? (
        <div className="grid-2" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
          <div className="stack" style={{ gap: 2 }}>
            <span className="tiny muted">Items</span>
            <span style={{ fontSize: 17, fontWeight: 600 }}>{plan.lines.length}</span>
          </div>
          <div className="stack" style={{ gap: 2 }}>
            <span className="tiny muted">Estimate</span>
            <span style={{ fontSize: 17, fontWeight: 600 }}>
              ~${Math.round(estimate)}
              {plan.unknownPriceCount ? '+' : ''}
            </span>
          </div>
          <div className="stack" style={{ gap: 2 }}>
            <span className="tiny muted">Delivery</span>
            <span style={{ fontSize: 17, fontWeight: 600 }}>{deliveryLabel(plan.deliveryHours)}</span>
          </div>
        </div>
      ) : (
        <div className="stack" style={{ gap: 6 }}>
          <div className="row-between small">
            <span>
              {money(plan.subtotal, 0)} of {money(plan.minimumOrder, 0)} minimum
            </span>
            <span className="muted">{plan.lines.length} items</span>
          </div>
          <div
            className="progress"
            role="progressbar"
            aria-label={`Progress to the ${plan.storeName} minimum`}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round((plan.subtotal / plan.minimumOrder) * 100)}
          >
            <div style={{ width: `${Math.min(100, (plan.subtotal / plan.minimumOrder) * 100)}%` }} />
          </div>
        </div>
      )}

      <span className="small muted">
        {summary}. {plan.reason}
      </span>

      {ready && (
        <>
          {linkStore && (
            <button type="button" className="btn btn-primary" onClick={open}>
              Open filled cart
              <Icon name="external" size={18} />
            </button>
          )}
          {result && (result.checklist.length > 0 || result.note) && (
            <div className="stack" style={{ gap: 6 }}>
              {result.note && <span className="small muted">{result.note}</span>}
              {result.checklist.length > 0 && !result.note && <span className="small muted">Not in the link yet (no product match). Add by hand:</span>}
              {result.checklist.map((c) => (
                <a key={c.name} href={c.url} target="_blank" rel="noopener" className="row small" style={{ minHeight: 44 }}>
                  <Icon name={c.product ? 'external' : 'search'} size={16} />
                  {c.qty} × {c.name}
                </a>
              ))}
            </div>
          )}
          <button type="button" className="btn" onClick={() => store.placeOrder(plan)}>
            I placed this order
          </button>
        </>
      )}

      {!ready &&
        plan.moveOptions.map((o) => {
          const target = stores.find((s) => s.id === o.storeId)?.name ?? o.storeId;
          const cost = o.extraCost > 0 ? ` (+$${o.extraCost.toFixed(2)})` : o.extraCost < 0 ? ` (saves $${(-o.extraCost).toFixed(2)})` : '';
          return (
            <button
              key={o.storeId}
              type="button"
              className="btn"
              onClick={() =>
                store.setItemStore(
                  plan.lines.map((l) => l.item.id),
                  o.storeId,
                )
              }
            >
              {o.complete ? `Move these to ${target} instead${cost}` : `Move what ${target} carries${cost}`}
            </button>
          );
        })}
    </div>
  );
}

export function OrderSheet() {
  const plan = useCartPlan();
  const list = useAppState((s) => s.list);
  const neededCount = list.filter((i) => i.status === 'needed').length;
  const [, force] = useState(0);
  const close = useCallback(() => navigate('/groceries'), []);

  return (
    <Sheet title="Order status" onClose={close}>
      <SheetHeader
        title="Order status"
        subtitle={`Checked just now · ${neededCount} item${neededCount === 1 ? '' : 's'} on the list`}
        onClose={close}
        extra={
          <button type="button" className="icon-btn" aria-label="Check again" onClick={() => force((n) => n + 1)}>
            <Icon name="refresh" size={20} />
          </button>
        }
      />

      {plan.stores.length === 0 && plan.unmatched.length === 0 && <div className="empty">Nothing to order. The list is empty.</div>}

      {plan.stores.map((s) => (
        <StoreCard key={s.storeId} plan={s} />
      ))}

      {plan.unmatched.length > 0 && (
        <div className="card stack">
          <div className="row-between">
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700 }}>Needs a store</span>
            <span className="badge badge-orange">{plan.unmatched.length}</span>
          </div>
          <span className="small muted">Choose where to buy these once; after that they route automatically.</span>
          {plan.unmatched.map((i) => (
            <Link key={i.id} to={`/groceries/item/${i.id}`} transition="none" className="row-between" style={{ minHeight: 44 }}>
              <span style={{ color: 'var(--text)' }}>
                {i.qty} × {i.name}
              </span>
              <span className="row small" style={{ gap: 4 }}>
                Choose <Icon name="chevron-right" size={16} />
              </span>
            </Link>
          ))}
        </div>
      )}

      <p className="small muted" style={{ margin: 0, lineHeight: 1.45 }}>
        Totals are estimates from past receipts. The store's own cart shows the real price.
      </p>
    </Sheet>
  );
}
